import { reactive, computed, getCurrentInstance } from "vue";
import dayjs from "dayjs";
import {
  listPositionPayRangeSubscriptions,
  listSubscriptionsForPosition,
  subscribePositionToPayRange,
  endPositionPayRangeSubscription,
} from "@/api/positionPayRangeSubscriptionAPI";
import { listPayRanges } from "@/api/payRangeAPI";
import { getChatterList } from "@/api/chatterAPI";
import { transformChatterData } from "@/utils/chatterUtils";
import { mapPayRangeListRow } from "@components/PayRangesListPage/mapPayRangeListRow";
import {
  defaultEffectiveDateForPublishedVersions,
  extractPublishedVersionRanges,
} from "@components/SubscribeToPayRangeDialog/subscribeToPayRangeUtils";
import { AS_OF_STATE } from "@components/PositionsWithPayRangeSubscriptionsTable/PositionsWithPayRangeSubscriptionsTable.vue";
import { defaultEffectiveTo } from "@components/UnsubscribeFromPayRangeDialog/unsubscribeFromPayRangeUtils";
import { mapPositionSubscriptionRow, mapSubscription } from "../mapPositionSubscriptionRow";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const PAY_RANGE_SEARCH_DEBOUNCE_MS = 300;

/**
 * Status filter options per Eric's spec (Slack, confirmed against the
 * Positions / Positions List View concept pages): Draft/Active/Retired are
 * the status-filter checkboxes (SearchModal's `statusOptions` — raw values,
 * used directly as both the checkbox key and the emitted filter value).
 * Archived is a separate opt-in dimension, not one of them — modeled as a
 * `filterGroups` radio, same convention as the legacy Positions view
 * (`usePositionViewState.js`).
 */
export const STATUS_OPTIONS = ["draft", "active", "retired"];

export const FILTER_GROUPS = [
  {
    label: "Archived",
    items: [
      { label: "Do not Show", value: "no_archived" },
      { label: "Show", value: "show_archived" },
    ],
  },
];

/** Documented default: Draft + Active selected, Archived hidden. */
const DEFAULT_STATUSES = ["active", "draft"];

/**
 * Human-readable text for the domain error codes the subscription API
 * returns (position_pay_range_subscription_service.py's error codes) —
 * ER-10840 AC: "a clear, documented error", not the raw code, on the UI.
 */
const ERROR_MESSAGES = {
  PAY_RANGE_NOT_FOUND: "That pay range could not be found.",
  PAY_RANGE_NOT_PUBLISHED:
    "This pay range isn't published yet — only published pay ranges can be subscribed to.",
    PAY_RANGE_VERSION_NOT_FOUND:
      "This pay range has no published version covering that date. Choose a different effective date.",
    SUBSCRIPTION_NOT_FOUND: "That subscription could not be found.",
    EFFECTIVE_TO_BEFORE_FROM:
      "The end date must be after the subscription start date.",
};

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function today() {
  return dayjs().format("YYYY-MM-DD");
}

/** Accept only a valid ISO `YYYY-MM-DD` URL value; anything else falls back to today. */
function normalizeAsOfDate(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = dayjs(value.trim());
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

function debounce(fn, delayMs) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}

/**
 * Which (if any) of a position's non-superseded subscriptions covers `date`.
 * Rule 4 (non-overlapping intervals) guarantees at most one match.
 */
function resolveExistingSubscription(subscriptions, date) {
  if (!date) return null;
  return (
    subscriptions.find((sub) => {
      if (!sub.effectiveFrom || date < sub.effectiveFrom) return false;
      return !sub.effectiveTo || date <= sub.effectiveTo;
    }) || null
  );
}

/** Picker option + full mapped pay range, keyed by id (for readiness/flag lookup on selection). */
function buildPayRangeOption(mappedRow) {
  const location = mappedRow.region
    ? `${mappedRow.operating_country} - ${mappedRow.region}`
    : mappedRow.operating_country;
  return {
    id: String(mappedRow.id),
    code: mappedRow.code,
    name: mappedRow.name,
    meta: location,
    flag: mappedRow.country_flag,
    readiness: {
      min: mappedRow.anchor_0,
      at50: mappedRow.anchor_50,
      at100: mappedRow.anchor_100,
      max: mappedRow.anchor_150,
    },
  };
}

/**
 * Positions → Pay Ranges tab page state (ER-10781): search, As-of date,
 * pagination, filters, and SubscribeToPayRangeDialog wiring.
 */
export function usePositionsPayRangesPageState({ initialAsOfDate = null } = {}) {
  const app = getCurrentInstance();
  const { proxy } = app || {};
  const todayValue = today();
  const restoredAsOfDate = normalizeAsOfDate(initialAsOfDate);

  const state = reactive({
    rows: [],
    loading: false,
    error: null,
    /** ER-12840 — 403 renders the page-level permission-denied state, not a toast. */
    forbidden: false,
    page: 1,
    pageSize: PAGE_SIZE,
    count: 0,
    search: "",
    asOfDate: restoredAsOfDate || todayValue,
    asOfState:
      restoredAsOfDate && restoredAsOfDate !== todayValue
        ? AS_OF_STATE.CUSTOM_DATE
        : AS_OF_STATE.TODAY,
    filters: {
      statuses: [...DEFAULT_STATUSES],
      showArchived: false,
      showFilterDialog: false,
    },
    subscribeDialog: {
      show: false,
      position: null,
      payRangeId: null,
      payRangeOptions: [],
      payRangeSearchLoading: false,
      selectedPayRangePreview: null,
      effectiveDate: null,
      subscriptions: [],
      existingSubscription: null,
      publishedVersionRanges: [],
      submitting: false,
    },
    unsubscribeDialog: {
      show: false,
      positionId: null,
      subscriptionId: null,
      payRangeName: "",
      payRangeCode: "",
      effectiveFrom: null,
      subscriptionEffectiveTo: null,
      effectiveTo: null,
      submitting: false,
    },
    chatterDrawer: {
      show: false,
      position: null,
      chatterData: [],
      total: 0,
      page: 1,
    },
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(state.count / state.pageSize)));

  /** One removable chip per selected status. Archived has no chip — it's the separate toggle. */
  const filterChips = computed(() =>
    state.filters.statuses.map((value) => ({ key: value, label: capitalize(value) }))
  );

  /**
   * SearchModal's `defaultSelected` — matched by either a checkbox's raw
   * value (status strings) or a radio item's label text (SearchModal reads
   * radios back by `.label`, not `.value` — see RadioInput.vue).
   */
  const filterModalDefaultSelected = computed(() => [
    ...state.filters.statuses,
    state.filters.showArchived ? "Show" : "Do not Show",
  ]);

  function notifySuccess(message) {
    if (proxy?.$notifications?.success) proxy.$notifications.success(message);
  }

  function notifyError(err, fallback) {
    const code = err?.response?.data?.error;
    const message = (code && ERROR_MESSAGES[code]) || code || err?.message || fallback;
    if (proxy?.$notifications?.error) proxy.$notifications.error(message);
    return message;
  }

  async function fetchRows() {
    state.loading = true;
    state.error = null;
    state.forbidden = false;
    try {
      const statuses = state.filters.showArchived
        ? [...state.filters.statuses, "archived"]
        : state.filters.statuses;
      const response = await listPositionPayRangeSubscriptions({
        asOf: state.asOfDate,
        page: state.page,
        pageSize: state.pageSize,
        name: state.search?.trim() || undefined,
        status: statuses,
      });
      const data = response?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      state.rows = results.map(mapPositionSubscriptionRow);
      state.count = data.count ?? results.length;
    } catch (err) {
      state.rows = [];
      state.count = 0;
      if (err?.response?.status === 403) {
        state.forbidden = true;
      } else {
        state.error = notifyError(err, "Failed to load positions");
      }
    } finally {
      state.loading = false;
    }
  }

  async function performSearch(value) {
    state.search = value == null ? "" : value;
    state.page = 1;
    await fetchRows();
  }

  const onUpdateSearch = debounce(performSearch, SEARCH_DEBOUNCE_MS);

  async function onUpdatePage(page) {
    state.page = page || 1;
    await fetchRows();
  }

  async function onUpdateAsOfDate(date) {
    state.asOfDate = date;
    state.asOfState = AS_OF_STATE.CUSTOM_DATE;
    state.page = 1;
    await fetchRows();
  }

  async function onResetToToday() {
    state.asOfDate = today();
    state.asOfState = AS_OF_STATE.TODAY;
    state.page = 1;
    await fetchRows();
  }

  function onOpenMoreFilters() {
    state.filters.showFilterDialog = true;
  }

  function closeFilterModal() {
    state.filters.showFilterDialog = false;
  }

  async function onToggleStatus(value) {
    const statuses = state.filters.statuses;
    const index = statuses.indexOf(value);
    if (index >= 0) {
      statuses.splice(index, 1);
    } else {
      statuses.push(value);
    }
    state.page = 1;
    await fetchRows();
  }

  async function onRemoveFilter(key) {
    await onToggleStatus(key);
  }

  /**
   * SearchModal's "Save Filter" — emits the combined checkbox + radio
   * selections. Checkbox items carry the raw status string as `.value`;
   * the Archived radio's `.value` is the selected item's *label* text
   * ("Show" / "Do not Show"), per RadioInput.vue's emit contract.
   */
  async function onFiltersApplied(items) {
    const values = (items || []).map((item) => item.value);
    state.filters.statuses = STATUS_OPTIONS.filter((status) => values.includes(status));
    state.filters.showArchived = values.includes("Show");
    state.filters.showFilterDialog = false;
    state.page = 1;
    await fetchRows();
  }

  // —— Position chatter ————————————————————————

  async function loadPositionChatter() {
    const position = state.chatterDrawer.position;
    if (!position) return;
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await getChatterList(
        accountId,
        position.id,
        "position",
        state.chatterDrawer.page
      );
      const results = response?.data?.results || [];
      state.chatterDrawer.chatterData.push(...transformChatterData(results));
      state.chatterDrawer.total = response?.data?.count ?? state.chatterDrawer.chatterData.length;
    } catch (err) {
      notifyError(err, "Failed to load chatter");
    }
  }

  function onOpenPositionChatter(row) {
    state.chatterDrawer.show = true;
    state.chatterDrawer.position = row;
    state.chatterDrawer.chatterData = [];
    state.chatterDrawer.total = 0;
    state.chatterDrawer.page = 1;
    loadPositionChatter();
  }

  function onCloseChatterDrawer() {
    state.chatterDrawer.show = false;
    state.chatterDrawer.position = null;
    state.chatterDrawer.chatterData = [];
    state.chatterDrawer.total = 0;
    state.chatterDrawer.page = 1;
  }

  function onRequestMoreChatter() {
    state.chatterDrawer.page += 1;
    loadPositionChatter();
  }

  // —— Subscribe-to-pay-range dialog ————————————————————————

  function resetSubscribeDialog() {
    state.subscribeDialog = {
      show: false,
      position: null,
      payRangeId: null,
      payRangeOptions: [],
      payRangeSearchLoading: false,
      selectedPayRangePreview: null,
      effectiveDate: null,
      subscriptions: [],
      existingSubscription: null,
      publishedVersionRanges: [],
      submitting: false,
    };
  }

  function onSubscribePosition(row) {
    state.subscribeDialog = {
      show: true,
      position: {
        id: String(row.id),
        code: String(row.code),
        name: String(row.name),
        department: String(row.department || ""),
      },
      payRangeId: null,
      payRangeOptions: [],
      payRangeSearchLoading: false,
      selectedPayRangePreview: null,
      effectiveDate: today(),
      subscriptions: [],
      existingSubscription: null,
      publishedVersionRanges: [],
      submitting: false,
    };
    searchPayRanges("");
    loadPositionSubscriptions(row.id);
  }

  /**
   * Fetches the position's non-superseded subscriptions (current + any
   * future-scheduled) so the conflict callout can catch an overlap with a
   * scheduled subscription — the row's own as-of-today subscription is
   * always null here (the dialog only opens from a gap row).
   */
  async function loadPositionSubscriptions(positionId) {
    try {
      const response = await listSubscriptionsForPosition(positionId);
      const results = Array.isArray(response?.data) ? response.data : [];
      state.subscribeDialog.subscriptions = results.map(mapSubscription);
      state.subscribeDialog.existingSubscription = resolveExistingSubscription(
        state.subscribeDialog.subscriptions,
        state.subscribeDialog.effectiveDate
      );
    } catch (err) {
      notifyError(err, "Failed to load the position's subscription history");
    }
  }

  async function searchPayRanges(query) {
    state.subscribeDialog.payRangeSearchLoading = true;
    try {
      // ER-12098 follow-up: free-text search goes through `search` (substring
      // over code + Job Family Level label), not the exact `code` filter — the
      // stored name was retired in ER-11730, so typing a name must still match.
      const response = await listPayRanges({
        status: ["PUBLISHED"],
        search: query || undefined,
        pageSize: 20,
      });
      const results = Array.isArray(response?.data?.results) ? response.data.results : [];
      const mapped = results.map((raw) => ({
        ...mapPayRangeListRow(raw),
        publishedVersionRanges: extractPublishedVersionRanges(raw),
      }));
      state.subscribeDialog.payRangeOptions = mapped.map(buildPayRangeOption);
      state.subscribeDialog._payRangesById = new Map(mapped.map((row) => [String(row.id), row]));
    } catch (err) {
      notifyError(err, "Failed to search pay ranges");
      state.subscribeDialog.payRangeOptions = [];
    } finally {
      state.subscribeDialog.payRangeSearchLoading = false;
    }
  }

  const onPayRangeSearch = debounce(searchPayRanges, PAY_RANGE_SEARCH_DEBOUNCE_MS);

  function onPayRangeChange(payRangeId) {
    state.subscribeDialog.payRangeId = payRangeId;
    const mapped = state.subscribeDialog._payRangesById?.get(String(payRangeId));
    state.subscribeDialog.selectedPayRangePreview = mapped ? buildPayRangeOption(mapped) : null;
    const ranges = mapped?.publishedVersionRanges || [];
    state.subscribeDialog.publishedVersionRanges = ranges;
    onUpdateEffectiveDate(
      defaultEffectiveDateForPublishedVersions(ranges, state.subscribeDialog.effectiveDate)
    );
  }

  function onUpdateEffectiveDate(date) {
    state.subscribeDialog.effectiveDate = date;
    state.subscribeDialog.existingSubscription = resolveExistingSubscription(
      state.subscribeDialog.subscriptions,
      date
    );
  }

  function closeSubscribeDialog() {
    resetSubscribeDialog();
  }

  async function onSubscribeSubmit({ payRangeId, effectiveDate }) {
    const position = state.subscribeDialog.position;
    if (!position) return;
    state.subscribeDialog.submitting = true;
    try {
      await subscribePositionToPayRange(position.id, {
        payRangeId,
        effectiveFrom: effectiveDate,
      });
      notifySuccess("Subscribed to pay range");
      closeSubscribeDialog();
      if (effectiveDate) {
        await onUpdateAsOfDate(effectiveDate);
      } else {
        await fetchRows();
      }
    } catch (err) {
      notifyError(err, "Failed to subscribe to pay range");
      state.subscribeDialog.submitting = false;
    }
  }

  function resetUnsubscribeDialog() {
    state.unsubscribeDialog = {
      show: false,
      positionId: null,
      subscriptionId: null,
      payRangeName: "",
      payRangeCode: "",
      effectiveFrom: null,
      subscriptionEffectiveTo: null,
      effectiveTo: null,
      submitting: false,
    };
  }

  function onUnsubscribePosition(row) {
    const subscription = row?.subscription;
    if (!row?.id || !subscription?.id || !subscription.effectiveFrom) return;
    state.unsubscribeDialog = {
      show: true,
      positionId: row.id,
      subscriptionId: subscription.id,
      payRangeName: String(subscription.payRangeName || ""),
      payRangeCode: String(subscription.payRangeCode || ""),
      effectiveFrom: subscription.effectiveFrom,
      subscriptionEffectiveTo: subscription.effectiveTo || null,
      effectiveTo: defaultEffectiveTo(subscription.effectiveFrom),
      submitting: false,
    };
  }

  function onUpdateUnsubscribeEffectiveTo(date) {
    state.unsubscribeDialog.effectiveTo = date;
  }

  async function onUnsubscribeSubmit({ effectiveTo }) {
    const { positionId, subscriptionId } = state.unsubscribeDialog;
    if (!positionId || !subscriptionId || !effectiveTo) return;
    state.unsubscribeDialog.submitting = true;
    try {
      await endPositionPayRangeSubscription(positionId, subscriptionId, {
        effectiveTo,
      });
      notifySuccess("Unsubscribed from pay range");
      resetUnsubscribeDialog();
      await fetchRows();
    } catch (err) {
      notifyError(err, "Failed to unsubscribe from pay range");
      state.unsubscribeDialog.submitting = false;
    }
  }

  return {
    state,
    pageCount,
    filterChips,
    filterModalDefaultSelected,
    fetchRows,
    onUpdateSearch,
    onUpdatePage,
    onUpdateAsOfDate,
    onResetToToday,
    onOpenMoreFilters,
    closeFilterModal,
    onFiltersApplied,
    onRemoveFilter,
    onOpenPositionChatter,
    onCloseChatterDrawer,
    onRequestMoreChatter,
    onSubscribePosition,
    onPayRangeSearch,
    onPayRangeChange,
    onUpdateEffectiveDate,
    onSubscribeSubmit,
    onSubscribeCancel: closeSubscribeDialog,
    onUnsubscribePosition,
    onUpdateUnsubscribeEffectiveTo,
    onUnsubscribeSubmit,
    onUnsubscribeCancel: resetUnsubscribeDialog,
  };
}
